import { HttpClient, HttpContext } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { DomainInterface, DomainProductDto, DomainStorageDto } from '../interfaces/domain-interface';
import { UserInterfaceDto } from '../interfaces/user-interface';
import { DestroyRef, Injectable } from '@angular/core';
import { ConfigurationService } from './configuration.service';
import { DomainPaginateInterface } from '../interfaces/dto/domain-paginate-interface';
import { RedirectionFromAppService } from './redirection-from-app.service';
import { OptionsInterface } from '../interfaces/options-interface';
import { CompanyService } from './company.service';
import { IGNORE_STATUSES } from '../http-context-tokens/ignored-statuses.http-context-token';
import { IGNORE_ALL_STATUSES } from 'src/app/core/constants/ignore-all-statuses';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
@Injectable({
  providedIn: 'root',
})
export class DomainsService {
  private readonly DOMAINS_STORAGE_KEY: string = 'domain_list';

  private readonly CURRENT_DOMAIN_KEY_STORAGE: string = 'current_domain_id';

  private readonly CURRENT_DOMAIN_CODE_STORAGE: string = 'current_domain_code';

  private readonly DOMAINS_ROUTE_API: string = 'api/v1.0/domains';

  private readonly DOMAIN_V2_ROUTE_API: string = 'api/v2.0/domains';

  public connectedUser$: BehaviorSubject<UserInterfaceDto | null> = new BehaviorSubject<UserInterfaceDto | null>(null);

  public currentDomain$: BehaviorSubject<DomainInterface | null> = new BehaviorSubject<DomainInterface | null>(null);

  public constructor(
    private readonly httpClient: HttpClient,
    private readonly configService: ConfigurationService,
    private readonly redirectionFromAppService: RedirectionFromAppService,
    private readonly companyService: CompanyService,
    private readonly destroyRef: DestroyRef,
  ) {}

  public getDomains(page: number = 1): Observable<DomainPaginateInterface> {
    return this.httpClient
      .get<DomainPaginateInterface>(`${this.configService.urlApiSiVision}/${this.DOMAINS_ROUTE_API}?page=${page}`)
      .pipe(
        tap((domains) => {
          sessionStorage.setItem(this.DOMAINS_STORAGE_KEY, JSON.stringify(domains));
          this.setCurrentDomainLocalstorage(domains.values);
        }),
      );
  }

  public clearAndGetDomains(page: number = 1): Observable<DomainPaginateInterface> {
    this.redirectionFromAppService.clear();
    return this.getDomains(page);
  }

  public getDomainsPaginate(page: number = 1): Observable<DomainPaginateInterface> {
    return this.httpClient.get<DomainPaginateInterface>(
      `${this.configService.urlApiSiVision}/${this.DOMAINS_ROUTE_API}?page=${page}`,
    );
  }

  public getDomainsSessionStorage(): DomainPaginateInterface {
    const jsonDomains = sessionStorage.getItem(this.DOMAINS_STORAGE_KEY);
    if (jsonDomains) {
      const domains: DomainPaginateInterface = JSON.parse(jsonDomains);
      this.setCurrentDomainLocalstorage(domains.values);
      return domains;
    }
    return {
      paginate: {
        totalPages: 0,
        currentPage: 0,
        dataPerPage: 0,
        totalDatas: 0,
      },
      values: [],
    };
  }

  public getCurrentDomain(): Observable<DomainInterface | null> {
    return this.currentDomain$.asObservable();
  }

  public changeCurrentDomain(idDomain: string) {
    this.getDomainById(idDomain)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((domain: DomainInterface) => {
        this.companyService.changeCurrentCompany(domain.code);
        sessionStorage.removeItem('PartnerCode');
        this.currentDomain$.next(domain);
        this.redirectionFromAppService.setRedirectionId(domain.id);
        sessionStorage.setItem(this.CURRENT_DOMAIN_KEY_STORAGE, domain.id);
        sessionStorage.setItem(this.CURRENT_DOMAIN_CODE_STORAGE, domain.code.toString());
        if (domain.domainProduct?.some((domainProduct: DomainProductDto) => domainProduct.partnerCode != null))
          sessionStorage.setItem(
            'PartnerCode',
            domain.domainProduct.find((domainProduct: DomainProductDto) => domainProduct.partnerCode !== null)
              ?.partnerCode ?? '',
          );
      });
  }

  public changeCurrentDomainFromHeaderOrNew(idDomain: string) {
    this.redirectionFromAppService.clear();
    this.changeCurrentDomain(idDomain);
  }

  public getDomainById(idDomain: string) {
    return this.httpClient.get<DomainInterface>(
      `${this.configService.urlApiSiVision}/${this.DOMAINS_ROUTE_API}/${idDomain}`,
    );
  }

  public getUserConnectedInfos(idDomain: string) {
    return this.httpClient
      .get<UserInterfaceDto>(`${this.configService.urlApiSiVision}/${this.DOMAINS_ROUTE_API}/${idDomain}/getMe`)
      .pipe(
        tap((user: UserInterfaceDto) => {
          if (user) {
            this.connectedUser$.next(user);
          }
        }),
      );
  }

  public unblockDomain(idDomain: string): Observable<void> {
    return this.httpClient.post<void>(
      `${this.configService.urlApiSiVision}/${this.DOMAINS_ROUTE_API}/${idDomain}/unlock`,
      null,
    );
  }

  public deleteDomain(idDomain: string) {
    return this.httpClient.delete<unknown>(
      `${this.configService.urlApiSiVision}/${this.DOMAINS_ROUTE_API}/${idDomain}`,
    );
  }

  public resetProductsData(domainId: string): Observable<unknown> {
    return this.httpClient.delete<unknown>(
      `${this.configService.urlApiSiVision}/${this.DOMAIN_V2_ROUTE_API}/${domainId}/reset-products-data`,
    );
  }

  public clearLocalStorageStorage() {
    sessionStorage.removeItem(this.DOMAINS_STORAGE_KEY);
    sessionStorage.removeItem(this.CURRENT_DOMAIN_KEY_STORAGE);
  }

  public clearLocalStorageDomain() {
    sessionStorage.removeItem(this.DOMAINS_STORAGE_KEY);
  }

  public optionsMinValues(domainId: string): Observable<OptionsInterface[]> {
    return this.httpClient.get<OptionsInterface[]>(
      `${this.configService.urlApiSiVision}/${this.DOMAINS_ROUTE_API}/${domainId}/GetMinOptions`,
    );
  }

  public getDomainStorage(idDomain: string, ignoreInterceptor: boolean): Observable<DomainStorageDto> {
    return this.httpClient.get<DomainStorageDto>(
      `${this.configService.urlApiSiVision}/${this.DOMAINS_ROUTE_API}/${idDomain}/storage`,
      {
        context: new HttpContext().set(IGNORE_STATUSES, ignoreInterceptor ? IGNORE_ALL_STATUSES : []),
      },
    );
  }

  private setCurrentDomainLocalstorage(domains: DomainInterface[]) {
    if (Array.isArray(domains) && domains.length > 0) {
      const domainId = this.redirectionFromAppService.RedirectionId;
      if (domainId) {
        this.changeCurrentDomain(domainId);
      } else {
        this.changeCurrentDomain(domains[0].id);
      }
    }
  }
}
